import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Button, IconButton, Link, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { theme } from '../../../theme/theme';
import { formateoFechasParaCaratulas, validarFuncionVigente } from 'utils/utilities';
import { useNavigate } from 'react-router-dom';
import { EspectaculoBanner } from 'utils/models/types';
import NoBanner from 'assets/banners/NoBanners.jpg';
import Spinner from 'components/public/Spinner/Spinner';

interface BannerProps {
  banners: EspectaculoBanner[];
  interval?: number;
}

const Banner = ({ banners, interval = 10000 }: BannerProps) => {
  const [indiceActual, setIndiceActual] = useState(0);
  const [transicion, setTransicion] = useState(false);
  const [imagenesLoaded, setImagenesLoaded] = useState(false);
  const [hayBannersFuncionales, setHayBannersFuncionales] = useState(false);
  const navigate = useNavigate();
  const transicionRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let bannersFuncionales = false;

    const checkimagenesLoaded = banners.map((banner, index) => {
      return new Promise<void>(resolve => {
        const img = new Image();
        img.src = banner.BannerUrl || NoBanner;
        img.onload = () => {
          if (!bannersFuncionales) {
            bannersFuncionales = true;
            setHayBannersFuncionales(true);
          }
          resolve();
        };
        img.onerror = () => {
          if (index === banners.length - 1 && !bannersFuncionales) {
            setHayBannersFuncionales(false);
          }
          resolve();
        };
      });
    });

    Promise.all(checkimagenesLoaded).then(() => setImagenesLoaded(true));
  }, [banners]);

  const handlePrevious = useCallback(() => {
    if (transicion) return;
    setTransicion(true);
    setIndiceActual(prevIndex => (prevIndex - 1 + banners.length) % banners.length);
    transicionRef.current = setTimeout(() => setTransicion(false), 500);
  }, [transicion, banners.length]);

  const handleNext = useCallback(() => {
    if (transicion) return;
    setTransicion(true);
    setIndiceActual(prevIndex => (prevIndex + 1) % banners.length);
    transicionRef.current = setTimeout(() => setTransicion(false), 500);
  }, [transicion, banners.length]);

  useEffect(() => {
    let autoPlay: NodeJS.Timeout | null = null;

    if (imagenesLoaded && hayBannersFuncionales) {
      autoPlay = setInterval(() => {
        handleNext();
      }, interval);
    }

    return () => {
      if (autoPlay) clearInterval(autoPlay);
    };
  }, [imagenesLoaded, hayBannersFuncionales, handleNext, interval]);

  useEffect(() => {
    return () => {
      if (transicionRef.current) clearTimeout(transicionRef.current);
    };
  }, []);

  const defaultBanner: EspectaculoBanner[] = [
    {
      BannerUrl: NoBanner,
      Nombre: '¡Regresá pronto!',
      Descripcion: 'No hay obras destacadas actualmente.',
      FechaInicio: new Date(),
      FechaFin: new Date(),
      Director: '',
      Id: 1,
    },
  ];

  const bannersAMostrar = hayBannersFuncionales ? banners : defaultBanner;

  return (
    <Box
      sx={{
        width: 1,
        overflow: 'hidden',
        paddingY: '12px',
        boxShadow: 'inset 0px 0px 50px rgba(0,0,0,0.9)',
      }}
    >
      <Box marginX="auto" sx={{ position: 'relative', height: '60vh', overflow: 'hidden' }}>
        {imagenesLoaded ? (
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: '100%',
              transform: `translateX(-${indiceActual * 100}%)`,
              transition: transicion ? 'transform 0.5s ease' : 'none',
            }}
          >
            {bannersAMostrar.map((item, index) => {
              const { BannerUrl, Nombre, Descripcion, FechaInicio, FechaFin, Director, Id } = item;

              const rangoFechas =
                FechaInicio && FechaFin && validarFuncionVigente(FechaFin)
                  ? formateoFechasParaCaratulas(FechaInicio, FechaFin)
                  : 'Sin próximas funciones';

              return (
                <Box
                  key={index}
                  sx={{
                    minWidth: '100%',
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={BannerUrl}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      boxShadow: 'inset 0px 0px 50px rgba(0,0,0,0.9)',
                    }}
                  />
                  <Box
                    height="calc(100% - 30px)"
                    minWidth="500px"
                    display="flex!important"
                    position="absolute"
                    padding="15px"
                    flexDirection="column"
                    justifyContent="center"
                    sx={{
                      top: '0',
                      left: { xs: '50%', md: '70%' },
                      transform: 'translate(-50%, 0)',
                      backgroundColor: { xs: 'transparent', sm: 'rgba(20, 20, 20, 0.6)' },
                    }}
                  >
                    <Box
                      mb={4}
                      px={6}
                      sx={{
                        maxHeight: { xs: '80%', md: 'unset' },
                        maxWidth: { xs: '50%', md: 'unset' },
                        marginX: { xs: 'auto', md: 'unset' },
                        overflow: { xs: 'hidden', md: 'unset' },
                      }}
                    >
                      <Typography variant="h4" color="white" sx={{ mb: 1 }} component="div">
                        {Nombre}
                      </Typography>
                      <Typography variant="body1" color="white" sx={{ mb: 2 }}>
                        {Descripcion}
                      </Typography>
                      <Typography variant="body1" color="white" fontWeight={600}>
                        {hayBannersFuncionales ? rangoFechas : ''}
                      </Typography>

                      {hayBannersFuncionales && (
                        <Typography variant="body1" color="white">
                          Dirección: <b>{Director}</b>
                        </Typography>
                      )}
                    </Box>
                    {hayBannersFuncionales && (
                      <Button
                        variant="contained"
                        component={Link}
                        onClick={() => navigate(`/espectaculos/${Id}`)}
                        sx={{
                          color: 'white',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                          },
                          backgroundColor: theme.palette.secondary.main,
                          maxWidth: '400px',
                          margin: '0 auto',
                        }}
                      >
                        {validarFuncionVigente(FechaFin) ? 'Comprar entradas' : 'Ver detalle'}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Spinner />
        )}

        <IconButton
          onClick={handlePrevious}
          aria-label="Banner anterior"
          sx={{
            position: 'absolute',
            top: '50%',
            left: 30,
            transform: 'translateY(-50%)',
            color: 'white',
          }}
          disabled={indiceActual === 0}
        >
          <ArrowBackIos />
        </IconButton>

        <IconButton
          onClick={handleNext}
          aria-label="Siguiente banner"
          sx={{
            position: 'absolute',
            top: '50%',
            right: 30,
            transform: 'translateY(-50%)',
            color: 'white',
          }}
          disabled={indiceActual === bannersAMostrar.length - 1}
        >
          <ArrowForwardIos />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Banner;
